import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, ChevronDown, ChevronRight, Eye, EyeOff,
  ExternalLink, FlaskConical, Loader2, Save, Sparkles, XCircle,
} from "lucide-react";
import {
  api,
  type IntegrationActionView,
  type IntegrationView,
  type SetupGuideView,
  type SmokeTestResultView,
} from "../lib/api.js";

/**
 * One credential card. Owns:
 *   - Per-field edit state (controlled inputs that diverge from the masked server value
 *     only when the operator types into them)
 *   - Save mutation (PATCHes the diff)
 *   - Test mutation (smoke-tests the credential)
 *
 * Inputs render password-style for sensitive fields and text-style for the rest.
 * Empty input → leaves the existing value in .env alone (we only PATCH dirty keys).
 */
export function IntegrationCard({ integration }: { integration: IntegrationView }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<SmokeTestResultView | null>(null);

  const dirty = Object.keys(drafts);

  const saveMutation = useMutation({
    mutationFn: (updates: Record<string, string>) => api.patchIntegration(integration.id, updates),
    onSuccess: () => {
      setDrafts({});
      setTestResult(null);
      qc.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.testIntegration(integration.id),
    onSuccess: (r) => setTestResult(r),
    onError: (e: Error) => setTestResult({ ok: false, error: e.message }),
  });

  const [activeAction, setActiveAction] = useState<string | null>(null);
  const runActionMutation = useMutation({
    mutationFn: (actionId: string) => api.runIntegrationAction(integration.id, actionId),
    onSuccess: (r) => {
      setTestResult(r);
      setActiveAction(null);
      qc.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (e: Error) => {
      setTestResult({ ok: false, error: e.message });
      setActiveAction(null);
    },
  });

  function setDraft(key: string, v: string) {
    setDrafts((d) => ({ ...d, [key]: v }));
  }

  return (
    <div className="card p-5">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h3 className="truncate text-sm font-semibold text-ink">{integration.name}</h3>
            <StatusPill configured={integration.configured} />
          </div>
          <p className="mt-1 text-xs text-muted">{integration.description}</p>
        </div>
        {integration.docsUrl && (
          <a
            href={integration.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 text-2xs font-medium text-brand-700 hover:underline"
          >
            Get keys
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </header>

      {integration.setupGuide && (
        <SetupGuide
          guide={integration.setupGuide}
          // Auto-expanded by default when the integration isn't configured yet
          // (first-run UX), collapsed once they've gotten through it.
          defaultOpen={!integration.configured}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {integration.fields.map((field) => {
          const draft = drafts[field.key];
          const isDirty = draft !== undefined;
          const isSelect = !!(field.options && field.options.length > 0);
          // For selects, the effective value is the operator's draft or the
          // currently-saved value (so the dropdown reflects current state
          // without forcing them to re-pick on every page load).
          const effectiveValue = isDirty ? draft : isSelect ? (field.value ?? "") : "";
          const placeholder =
            field.set && !isDirty
              ? field.value ?? "***"
              : field.placeholder ?? "";
          const isSensitive = field.sensitive ?? true;
          const inputType = isSensitive && !(showSecret[field.key] ?? false) ? "password" : "text";

          return (
            <div key={field.key}>
              <label className="label flex items-center justify-between">
                <span>
                  {field.label}
                  {field.required && <span className="ml-1 text-danger">*</span>}
                </span>
                {isSensitive && isDirty && !isSelect && (
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => ({ ...s, [field.key]: !s[field.key] }))}
                    className="inline-flex items-center gap-0.5 text-2xs uppercase tracking-wide text-muted hover:text-ink"
                  >
                    {(showSecret[field.key] ?? false) ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {(showSecret[field.key] ?? false) ? "Hide" : "Show"}
                  </button>
                )}
              </label>
              {isSelect ? (
                <select
                  value={effectiveValue}
                  onChange={(e) => setDraft(field.key, e.target.value)}
                  className="input text-sm"
                >
                  {/* Empty option only when no value is selected yet */}
                  {!effectiveValue && <option value="">(choose…)</option>}
                  {field.options!.map((opt) => (
                    <option key={opt.value} value={opt.value} title={opt.description}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={inputType}
                  value={isDirty ? draft : ""}
                  placeholder={placeholder}
                  onChange={(e) => setDraft(field.key, e.target.value)}
                  className="input font-mono text-sm"
                  autoComplete="off"
                  spellCheck={false}
                />
              )}
              {field.description && (
                <p className="mt-1 text-xs text-muted">{field.description}</p>
              )}
              {field.set && !isDirty && !isSelect && (
                <p className="mt-1 text-[11px] text-muted">
                  current: <span className="font-mono">{field.value ?? "***"}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      <footer className="mt-5 flex items-center justify-between gap-3">
        <div className="flex-1">
          {testResult && <TestResultBanner result={testResult} />}
          {runActionMutation.isPending && activeAction && (
            <ActionInProgressBanner actionId={activeAction} integration={integration} />
          )}
        </div>
        <div className="flex items-center gap-2">
          {(integration.actions ?? []).map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              dirty={dirty.length > 0}
              busy={runActionMutation.isPending && activeAction === action.id}
              anyOtherBusy={(runActionMutation.isPending && activeAction !== action.id) || testMutation.isPending}
              onClick={() => {
                setActiveAction(action.id);
                setTestResult(null);
                runActionMutation.mutate(action.id);
              }}
            />
          ))}
          {integration.hasTest && (
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={!integration.configured || testMutation.isPending || runActionMutation.isPending || dirty.length > 0}
              className="btn-secondary btn-sm gap-1.5"
              title={dirty.length > 0 ? "Save changes first" : !integration.configured ? "Required fields not configured" : "Run smoke test"}
            >
              <FlaskConical className="h-3.5 w-3.5" />
              {testMutation.isPending ? "Testing…" : "Test"}
            </button>
          )}
          <button
            type="button"
            onClick={() => saveMutation.mutate(drafts)}
            disabled={dirty.length === 0 || saveMutation.isPending}
            className="btn-primary btn-sm gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {saveMutation.isPending ? "Saving…" : dirty.length === 0 ? "Save" : `Save ${dirty.length}`}
          </button>
        </div>
      </footer>
      {saveMutation.isError && (
        <p className="mt-2 text-xs text-danger">Save failed: {(saveMutation.error as Error).message}</p>
      )}
    </div>
  );
}

function SetupGuide({ guide, defaultOpen }: { guide: SetupGuideView; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const stepCount = guide.sections.reduce((acc, s) => acc + s.steps.length, 0);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied((c) => (c === text ? null : c)), 1500);
    } catch { /* clipboard unavailable, ignore */ }
  };

  return (
    <div className="mb-4 rounded-lg border border-line bg-canvas">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold text-ink hover:bg-line-soft"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
          Setup instructions
          <span className="text-2xs font-normal text-muted">({stepCount} steps)</span>
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-line px-4 py-3">
          {guide.intro && <p className="text-xs text-muted">{guide.intro}</p>}
          {guide.sections.map((section, i) => (
            <div key={i}>
              <div className="text-2xs font-semibold uppercase tracking-wider text-ink">
                {section.title}
                {section.subtitle && (
                  <span className="ml-2 font-normal text-muted">— {section.subtitle}</span>
                )}
              </div>
              <ol className="mt-1.5 space-y-1.5">
                {section.steps.map((step, j) => (
                  <li key={j} className="flex gap-2 text-xs text-ink">
                    <span className="shrink-0 select-none font-mono text-muted">{j + 1}.</span>
                    <div className="min-w-0 flex-1">
                      <span>{step.text}</span>
                      {step.link && (
                        <a
                          href={step.link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center gap-0.5 text-2xs font-medium text-brand-700 hover:underline"
                        >
                          {step.link.label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {step.code && (
                        <div className="mt-1 flex items-start gap-1 rounded bg-white px-2 py-1 ring-1 ring-line">
                          <code className="flex-1 break-all font-mono text-[11px] text-ink">{step.code}</code>
                          <button
                            type="button"
                            onClick={() => copy(step.code!)}
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted hover:bg-line hover:text-ink"
                            title="Copy"
                          >
                            {copied === step.code ? "Copied" : "Copy"}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionButton({
  action,
  dirty,
  busy,
  anyOtherBusy,
  onClick,
}: {
  action: IntegrationActionView;
  dirty: boolean;
  busy: boolean;
  anyOtherBusy: boolean;
  onClick: () => void;
}) {
  const disabled = dirty || !action.ready || busy || anyOtherBusy;
  let title: string;
  if (dirty) title = "Save changes first";
  else if (!action.ready) {
    title = action.disabledHint
      ?? `Set ${action.requiresFields.join(", ")} first, then click Save`;
  } else if (action.description) {
    title = action.description;
  } else {
    title = action.label;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="btn-secondary btn-sm gap-1.5"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      {busy ? `${action.label}…` : action.label}
    </button>
  );
}

function ActionInProgressBanner({ actionId, integration }: { actionId: string; integration: IntegrationView }) {
  const action = integration.actions?.find((a) => a.id === actionId);
  // YouTube-specific helper text since the OAuth flow is non-obvious.
  const isYoutubeAuth = integration.id === "youtube" && actionId === "authenticate";
  return (
    <div className="flex items-start gap-2 rounded-lg bg-brand/5 px-3 py-1.5 text-xs text-brand-700">
      <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />
      <div className="min-w-0">
        <div className="font-medium">{action?.label ?? "Running"}…</div>
        {isYoutubeAuth ? (
          <div className="mt-0.5 text-2xs text-muted">
            A browser tab should open for Google's consent screen. If it doesn't, check the server logs for the auth URL.
            The MCP listens on 127.0.0.1:3456 for the redirect.
          </div>
        ) : action?.description ? (
          <div className="mt-0.5 text-2xs text-muted">{action.description}</div>
        ) : null}
      </div>
    </div>
  );
}

function StatusPill({ configured }: { configured: boolean }) {
  return configured ? (
    <span className="pill-ok">
      <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
      Configured
    </span>
  ) : (
    <span className="pill-muted">Not configured</span>
  );
}

function TestResultBanner({ result }: { result: SmokeTestResultView }) {
  if (result.ok) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-accent-soft px-3 py-1.5 text-xs text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} />
        <span className="truncate text-ink">{result.summary ?? "Smoke test passed"}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-1.5 text-xs">
      <XCircle className="h-3.5 w-3.5 shrink-0 text-danger" strokeWidth={2.4} />
      <span className="truncate text-ink">{result.error ?? "Smoke test failed"}</span>
    </div>
  );
}
